<template>
  <v-container class="px-xl-7 bg-gainsboro" fluid>
    <div v-if="getLoading" class="d-flex justify-center align-center">
      <div class="white rounded-xl my-3 pa-2">
        Få et overblik over kirurgen ...
      </div>
    </div>
    <div v-else-if="overviewData">
      <v-row class="my-0 align-start">
        <v-col cols="12" lg="12">
          <h2 class='text-subtitle-1 text-uppercase mb-2'>Forslået visitering</h2>
          <div class='d-flex align-center ga-2'>
            <span class='mr-2'>Status:</span>
            <div>
              <v-select class="my-0 py-0 overview-data__clear-select" v-model="status" :items="statusTypes" item-text="label" item-value="key" single-line hide-details append-icon="mdi-menu-down">
                <template v-slot:selection="{ item, index }">
                  <span :class="[item.color, 'status-label', 'mr-2']"/>
                  <span class="text-body-2 text-truncate d-block" style="max-width: 130px">{{ item.label }}</span>
                </template>
                <template v-slot:item="{ item, index }">
                  <span :class="[item.color, 'status-label', 'mr-2']"/> <span class="text-body-2">{{ item.label }}</span>
                </template>
              </v-select>
            </div>
            <v-tooltip v-if='overviewData.isAISuggestionStatus' bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class='ml-2' size='23' v-bind="attrs" v-on="on">mdi-auto-fix</v-icon>
              </template>
              <span>AI-genereret</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
      <v-row class="my-0 align-start">
        <v-col cols="12" lg="4" class='d-flex align-center'>
          <v-select :items="treatmentTypes" item-text="name" item-value="id" label="Vælg behandlingstype" v-model="treatmentType">
            <template v-slot:item="{ item, on }">
              <v-list-item v-on="on" v-show="item.id !== -100">
                <span>{{ item.name }}</span>
              </v-list-item>
            </template>
          </v-select>
          <v-tooltip v-if='overviewData.isAISuggestionTreatmentType' bottom :allow-overflow="true">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class='ml-2' size='23' v-bind="attrs" v-on="on">mdi-auto-fix</v-icon>
            </template>
            <span>AI-genereret</span>
          </v-tooltip>
        </v-col>
        <v-col cols="12" lg="4" class='d-flex align-center'>
          <v-select :items="surgeons" item-text="fullName" item-value="id" label="Behandler" loader-height="1" v-model="surgeonId"/>
          <v-tooltip v-if='overviewData.isAISuggestionSurgeon' bottom :allow-overflow="true">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class='ml-2' size='23' v-bind="attrs" v-on="on">mdi-auto-fix</v-icon>
            </template>
            <span>AI-genereret</span>
          </v-tooltip>
        </v-col>
        <v-col cols="12" lg="4" class='d-flex align-center'>
          <v-text-field label="Tidsplan" append-icon="mdi-timer-outline" v-model="operationDuration"/>
          <v-tooltip v-if='overviewData.isAISuggestionOperationDuration' bottom :allow-overflow="true">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class='ml-2' size='23' v-bind="attrs" v-on="on">mdi-auto-fix</v-icon>
            </template>
            <span>AI-genereret</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <div class='white rounded-xl mb-4'>
        <div class='pa-4'>
          <v-tooltip bottom :allow-overflow="true">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class='mr-2' size='23' v-bind="attrs" v-on="on">mdi-auto-fix</v-icon>
            </template>
            <span>AI-genereret</span>
          </v-tooltip>
          {{ overviewData.suggestionExplanation }}
        </div>
        <div class='px-4 pt-4 grey lighten-4'>
          <v-textarea v-model="comment" label="Kommentar til AI" auto-grow :rows="3" outlined hide-details/>
        </div>
        <div class='pa-4 grey lighten-4 rounded-b-xl d-flex align-start'>
          <v-textarea v-model="surgeonComment" label="Bemærkninger" auto-grow :rows="3" outlined hide-details class="flex-grow-1"/>
          <v-tooltip v-if='overviewData.isAISuggestionSurgeonComment' bottom :allow-overflow="true">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class='ml-2 mt-2' size='23' v-bind="attrs" v-on="on">mdi-auto-fix</v-icon>
            </template>
            <span>AI-genereret</span>
          </v-tooltip>
        </div>
      </div>
      <v-row class="my-0 align-start mb-8">
        <v-col cols="12" class='d-flex justify-start'>
          <v-btn color="primary" depressed @click="saveOverview" :loading="saveLoading">
            Godkend
          </v-btn>
        </v-col>
      </v-row>
      <h2 class='text-subtitle-1 text-uppercase mb-2'>Opsummering af henvisning</h2>
      <div class='white rounded-xl mb-8 pa-5'>
        <table class='info-table'>
          <tr>
            <td>Klinik:</td>
            <td>{{ overviewData.clinitName }}</td>
          </tr>
          <tr>
            <td>Henvisernavn:</td>
            <td>{{ overviewData.doctorName }}</td>
          </tr>
          <tr>
            <td>Henvisningstype:</td>
            <td>{{ overviewData.referenceTypeName }}</td>
          </tr>
          <tr v-if="overviewData.patientAge != null">
            <td>Patientens alder:</td>
            <td>{{ overviewData.patientAge }}</td>
          </tr>
          <tr>
            <td>Indkaldelse:</td>
            <td>{{ overviewData.callType }}</td>
          </tr>
          <tr>
            <td>Betaling:</td>
            <td>{{ overviewData.paymentType }}</td>
          </tr>
          <tr v-if="overviewData.insuranceCompany">
            <td>Forsikringsselskab:</td>
            <td>{{ overviewData.insuranceCompany }}</td>
          </tr>
          <tr v-if="overviewData.policeClaim">
            <td>Police-/skadenummer:</td>
            <td>{{ overviewData.policeClaim }}</td>
          </tr>
          <tr v-if="overviewData.paymentRegionNumber">
            <td>Betaling EAN nr.:</td>
            <td>{{ overviewData.paymentRegionNumber }}</td>
          </tr>
          <tr v-if="overviewData.paymentRegionInstitution">
            <td>Betaling Institution:</td>
            <td>{{ overviewData.paymentRegionInstitution }}</td>
          </tr>
          <tr v-if="overviewData.paymentRegionAddress">
            <td>Betaling Adresse:</td>
            <td>{{ overviewData.paymentRegionAddress }}</td>
          </tr>
          <tr v-if="overviewData.paymentRegionZipAndCity">
            <td>Betaling Postnr./By:</td>
            <td>{{ overviewData.paymentRegionZipAndCity }}</td>
          </tr>
          <tr>
            <td>Diagnose:</td>
            <td>{{ overviewData.diagnose }}</td>
          </tr>
          <tr>
            <td>Tidligere behandling:</td>
            <td>{{ overviewData.previousTreatment }}</td>
          </tr>
          <tr>
            <td>Ønsker behandling:</td>
            <td>{{ overviewData.desiredTreatment }}</td>
          </tr>
          <tr>
            <td>Evt. kommentar:</td>
            <td>{{ overviewData.comment }}</td>
          </tr>
          <tr>
            <td>Sygesikring af DANMARK:</td>
            <td>{{ overviewData.healthInsuranceType }}</td>
          </tr>
          <tr v-if="overviewData.appropriationPdfSummary">
            <td>Pdf-resumé:</td>
            <td class="pdf-summary-cell">{{ overviewData.appropriationPdfSummary }}</td>
          </tr>
          <tr v-if="overviewData.appropriationPdfSummary">
            <td colspan="2" style="text-align: center;"><strong>AI kan tage fejl. Tjek venligst vigtige oplysninger.</strong></td>
          </tr>
        </table>
      </div>
      <h2 class='text-subtitle-1 text-uppercase mb-2'>Vedhæftede filer</h2>
      <div class='photos'>
        <OverviewXrayPreview v-for="item in overviewData.radiographFiles" :key='item.radiographId' :attachment="item" :visitItem="visitItem"/>
      </div>
      <v-row class="my-0 align-start">
        <v-col cols="12" lg="3">
          <div :class="[captionClasses]">Journal/doc.:</div>
          <div v-if="overviewData.journalFiles && overviewData.journalFiles.length > 0">
            <div v-for="fileItem in overviewData.journalFiles">
              <v-chip class="mb-1" color="white" @click="downloadJournal(visitItem.id, fileItem.journalId)">
                <v-icon size="20" class='mr-2'>icon-download</v-icon>
                <div class="text-truncate" style="max-width: 100%">{{ fileItem.fileName }}</div>
              </v-chip>
            </div>
          </div>
          <div v-else class="text-body-1 grey--text">Ingen data...</div>
        </v-col>
        <v-col cols="12" lg="3">
          <div :class="[captionClasses]">Godkendelse/Bevilling</div>
          <v-chip class="mb-1" v-if="overviewData.appropriationFileName" color="white" @click="downloadAppropriation(visitItem.id)">
            <v-icon size="20" class='mr-2'>icon-download</v-icon>
            <span class="text-truncate" style="max-width: 100%">{{ overviewData.appropriationFileName }}</span>
          </v-chip>
          <div v-else class="text-body-1 grey--text">Ingen data...</div>
        </v-col>
        <v-col cols="12" lg="3">
          <span :class="[captionClasses]">DICOM filer</span>
          <div v-if="overviewData.dicomFiles && overviewData.dicomFiles.length > 0">
            <div v-for="fileItem in overviewData.dicomFiles">
              <v-chip class="mb-1" color="white" @click="downloadRadiograph(visitItem.id, fileItem.radiographId)">
                <v-icon size="20" class='mr-2'>icon-download</v-icon>
                <div class="text-truncate" style="max-width: 100%">{{ fileItem.fileName }}</div>
              </v-chip>
            </div>
          </div>
          <div v-else class="text-body-1 grey--text">Ingen data...</div>
        </v-col>
        <v-col cols="12" lg="3">
          <span :class="[captionClasses]">ZIP filer</span>
          <div v-if="overviewData.zipFiles && overviewData.zipFiles.length > 0">
            <div v-for="fileItem in overviewData.zipFiles">
              <v-chip class="mb-1" color="white" @click="downloadRadiograph(visitItem.id, fileItem.radiographId)">
                <v-icon size="20" class='mr-2'>icon-download</v-icon>
                <div class="text-truncate" style="max-width: 100%">{{ fileItem.fileName }}</div>
              </v-chip>
            </div>
          </div>
          <div v-else class="text-body-1 grey--text">Ingen data...</div>
        </v-col>
      </v-row>
      <v-row class="my-0 align-start">
        <v-col cols="12" class='d-flex justify-end'>
          <v-btn color="primary" depressed @click="saveOverview" :loading="saveLoading">
            Godkend
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
  import {COMMON_TYPES} from '@/constants/commonTypes';
  import OverviewXrayPreview from '@/components/overview/OverviewXrayPreview';
  import api from '@/api/api';
  import methods from '@/utils/methods';

  export default {
    name: 'OverviewHandling',

    components: {OverviewXrayPreview},

    props: ['visitItem'],

    data: function () {
      return {
        getLoading: true,
        saveLoading: false,
        overviewData: null,
        status: 1,
        treatmentType: null,
        surgeonId: null,
        operationDuration: '',
        comment: '',
        surgeonComment: ''
      };
    },

    watch: {
      visitItem: function(newValue, oldValue) {
        newValue.surgeonComment ? this.surgeonComment = newValue.surgeonComment : this.surgeonComment = '';
      }
    },

    computed: {
      captionClasses: function () {
        return [...COMMON_TYPES.CAPTION_CLASSES]
      },
      statusTypes: function () {
        return this.$store.state.overview.statusTypes;
      },
      treatmentTypes: function () {
        return this.$store.state.treatmentTypes;
      },
      surgeons: function () {
        return this.$store.state.surgeons;
      },
      radiographs: function () {
        return this.overviewData?.radiographFiles?.filter(item => item.fileType === 4) || [];
      },
      attachments: function () {
        return this.overviewData?.radiographFiles?.filter(item => item.fileType !== 4) || [];
      },
    },

    mounted: function () {
      this.getOverview();
    },

    methods: {
      downloadJournal: function (visitId, journalId) {
        methods.downloadURL(api.journalsFileUrl(visitId, journalId));
      },
      downloadAppropriation: function (visitId) {
        methods.downloadURL(api.appropriationFileUrl(visitId));
      },
      downloadRadiograph: function (visitId, radiographId) {
        methods.downloadURL(api.radiographFileUrl(visitId, radiographId));
      },
      setOverviewData: function() {
        if (this.overviewData.suggestedVisitStatus >= 0 && this.overviewData.suggestedVisitStatus !== null) {
          this.status = this.overviewData.suggestedVisitStatus;
        } else {
          this.status = this.overviewData.currentVisitStatus;
        }
        if (this.overviewData.suggestedTreatmentTypeId >= 0 && this.overviewData.suggestedTreatmentTypeId !== null) {
          this.treatmentType = this.overviewData.suggestedTreatmentTypeId;
        } else {
          this.treatmentType = this.overviewData.currentTreatmentTypeId;
        }
        if (this.overviewData.suggestedSurgeonId >= 0 && this.overviewData.suggestedSurgeonId !== null) {
          this.surgeonId = this.overviewData.suggestedSurgeonId;
        } else if (this.overviewData.surgeonId >= 0 && this.overviewData.surgeonId !== null) {
          this.surgeonId = this.overviewData.surgeonId;
        }
        if (this.overviewData.suggestedOperationDuration !== null && this.overviewData.suggestedOperationDuration !== '') {
          this.operationDuration = this.overviewData.suggestedOperationDuration;
        } else if (this.overviewData.operationDuration) {
          this.operationDuration = this.overviewData.operationDuration;
        }
        if (this.overviewData.aiClassificationSurgeonComment) {
          this.comment = this.overviewData.aiClassificationSurgeonComment;
        }
        if (this.overviewData.suggestedSurgeonComment !== null && this.overviewData.suggestedSurgeonComment !== '') {
          this.surgeonComment = this.overviewData.suggestedSurgeonComment;
        } else if (this.overviewData.surgeonComment) {
          this.surgeonComment = this.overviewData.surgeonComment;
        }
      },
      saveQueryData: function () {
        let queryData = [];
        if (this.status >= 0) {
          queryData.push({op: 'replace', path: '/status', value: this.status});
        }
        if (this.treatmentType >= 0) {
          queryData.push({op: 'replace', path: '/treatmentType', value: this.treatmentType});
        }
        if (this.surgeonId >= 0) {
          queryData.push({op: 'replace', path: '/surgeonId', value: this.surgeonId});
        }
        if (this.operationDuration) {
          queryData.push({op: 'replace', path: '/operationDuration', value: this.operationDuration});
        }
        if (this.comment) {
          queryData.push({op: 'replace', path: '/aiClassificationSurgeonComment', value: this.comment});
        }
        queryData.push({op: 'replace', path: '/surgeonComment', value: this.surgeonComment});
        return queryData;
      },
      getOverview: async function () {
        try {
          this.getLoading = true;
          const overviewData = await api.getSurgeonOverview(this.visitItem.id);
          this.overviewData = overviewData.data;
          this.setOverviewData();
        } catch (e) {} finally {
          this.getLoading = false;
        }
      },
      saveOverview: async function () {
        try {
          this.saveLoading = true;
          await api.updateVisitPatch(this.saveQueryData(), this.visitItem.id, null);
          let newVisit = await api.getVisitInfo(this.visitItem.id);
          const overviewData = await api.getSurgeonOverview(this.visitItem.id);
          this.overviewData = overviewData.data;
          this.setOverviewData();
          this.$emit('updateVisitItem', newVisit.data);
          this.$emit('updateCommonVisitInfo', newVisit.data);
        } catch (e) {
          methods.showGlobalSnackbar('error', 'Server error. Noget gik galt. Prøv igen senere.');
        } finally {
          this.saveLoading = false;
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .info-table {
    border-collapse: collapse;
    width: 100%;

    tr {
      &:nth-child(2n) {
        background: #f5f5f5;
      }
    }
    td {
      padding: 5px 10px;

      &:first-child {
        width: 200px;
      }
    }
  }

  .pdf-summary-cell {
    white-space: pre-line;
  }

  .photos {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 20px;
    margin: 0 0 32px;
  }
</style>
